import React, {ChangeEvent, useEffect, useMemo, useState} from "react";
import {Button, Col, Dropdown, Form, Modal, Nav, Row, Tab} from "react-bootstrap";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import {BsPlus, BsX} from "react-icons/bs";
import {CiWarning} from "react-icons/ci";
import {Link, useSearchParams} from "react-router-dom";
import {useOverlayContext} from "../../contexts/OverlayContext";
import {useIDGenerator} from "../../hooks/useIDGenerator";
import {useFileOverlayExamples} from "../../hooks/useJsonLoader";
import {OverlayType} from "../../types/enums";
import {OverlayExample} from "../../types/overlay";
import {FormControlElement} from "../../types/Types";
import {getContrastColor} from "../../utils/colour";
import {customOverlayLabel, emptyCustomOverlayData} from "../../utils/overlay";

const keyName = (index: number) => `custom-${index + 1}`;

const OverlayModal = () => {
  const {
    overlayResults,
    setOverlayType,
    setOverlayScreen,
    customOverlayLists,
    setCustomOverlayLists,
    triggerProcessOverlay,
    modalDisplay,
    showModal,
    hideModal
  } = useOverlayContext();

  const {nextID, resetID} = useIDGenerator(0);
  const examples = useFileOverlayExamples();

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeKey, setActiveKey] = useState(keyName(0));

  const duplicateInfo = useMemo(() => {
    const seen = new Map<string, number>();
    const info = new Map<number, number>();
    customOverlayLists.forEach((list, i) => {
      const key = list.parsedGenes.slice().sort().join(',');
      if (seen.has(key)) {
        info.set(i, seen.get(key)!);
      } else {
        seen.set(key, i);
      }
    });
    return info;
  }, [customOverlayLists]);

  useEffect(() => {
    // Automatically show modal if no results present or if no query present in URL
    if (!overlayResults && !searchParams.get('s')) {
      showModal();
    } else {
      hideModal();
    }
  }, []);

  /**
   * Initialize customOverlayLists with an empty list if none exist, usually on first access
   */
  useEffect(() => {
    if (customOverlayLists.length === 0) {
      setCustomOverlayLists([emptyCustomOverlayData(0)]);
    }
  }, [customOverlayLists, setCustomOverlayLists, nextID]);

  /**
   * Handles the addition of a new custom overlay list.
   * Generates a new unique ID and creates an empty custom overlay list object.
   * Updates the state by appending the new list to the existing custom overlay lists state.
   * Sets the active key to the key associated with the newly added list.
   */
  const handleAddList = () => {
    const id = nextID();
    setCustomOverlayLists([...customOverlayLists, emptyCustomOverlayData(id)]);
    setActiveKey(keyName(id));
  };

  /**
   * Handles the removal of a list item with the specified identifier from the custom overlay lists.
   * Updates the state by filtering out the list item with the given ID.
   * If the removed list is currently active and there are other lists remaining, updates the active key to the
   * first remaining list's key.
   */
  const handleRemoveList = (id: number) => {
    setCustomOverlayLists(customOverlayLists.filter((l) => l.id !== id));
    if (activeKey === keyName(id) && customOverlayLists.length > 1) {
      setActiveKey(keyName(customOverlayLists[0].id));
    }
  };

  /**
   * Drops current data and resets the modal to the default configuration.
   */
  const handleReset = () => {
    const id = resetID(); // Back to start value
    setCustomOverlayLists([emptyCustomOverlayData(id)]);
    setActiveKey(keyName(id));
  }

  /**
   * Updates the name of a custom overlay list, or sets it to a default value if the input is empty.
   */
  const handleNameChange = (listId: number) => {
    return (e: ChangeEvent<FormControlElement>) => {
      setCustomOverlayLists(customOverlayLists.map(l =>
        l.id === listId
          ? {...l, name: e.target.value || customOverlayLabel(listId)}
          : l
      ));
    }
  }

  /**
   * Parse input text, split to individual items, removing duplicates, and updating the relevant list.
   *
   * The function performs the following:
   * 1. Splits the input text into individual items based on specified delimiters (spaces, commas, semicolons).
   * 2. Trims whitespace and filters out empty strings.
   * 3. Eliminates duplicate gene names using case-insensitive comparison, while preserving the original casing of the
   *      first occurrence.
   * 4. Updates the appropriate list in the state with the original text and the parsed unique gene items.
   */
  const handleGeneListChange = (listId: number) => {
    return (e: ChangeEvent<FormControlElement>) => {
      const text = e.target.value;
      const items: string[] = [];
      const seen = new Set<string>();

      text
        .split(/[\s,;]+/)
        .map(s => s.trim())
        .filter(s => s.length > 0)
        .forEach(s => {
          const lower = s.toLowerCase();
          if (!seen.has(lower)) {
            seen.add(lower);
            items.push(s); // keep original casing of first occurrence
          }
        });

      setCustomOverlayLists(customOverlayLists.map(l =>
        l.id === listId
          ? {...l, genes: text, parsedGenes: items}
          : l
      ));
    }
  };

  /**
   * Handle the change of a colour input field value for a specific list.
   */
  const handleColourChange = (listId: number) => {
    return (e: ChangeEvent<FormControlElement>) => {
      setCustomOverlayLists(
        customOverlayLists.map(l => l.id === listId ? {...l, colour: e.target.value} : l))
    }
  }

  /**
   * Fill in the gene list with a predefined example.
   */
  const handleExample = (listId: number, example: OverlayExample) => {
    return () => {
      setCustomOverlayLists(
        customOverlayLists.map(l => l.id === listId
          ? {
            ...l,
            genes: example.genes.join('\n'),
            parsedGenes: example.genes,
            colour: example.colour,
            name: example.name
          }
          : l))
    }
  }

  const handleSubmit = (type: OverlayType) => {
    return () => {
      setOverlayScreen(null);
      setSearchParams({}); // Clear URL search params

      setOverlayType(type);
      triggerProcessOverlay();
      hideModal();
    }
  }

  const checkEntryForWarnings = (listIdx: number, parsedGenes: Array<any>) => {
    const isDuplicate = duplicateInfo.get(listIdx);

    if (isDuplicate !== undefined) {
      return `Duplicate of: ${customOverlayLists[isDuplicate].name}`;
    } else if (parsedGenes.length <= 2) {
      return "Gene list too short";
    }
  }

  const hasWarnings = useMemo(() =>
    customOverlayLists.some((list, i) => checkEntryForWarnings(i, list.parsedGenes)),
    [customOverlayLists])

  return (
    <>
      <Modal show={modalDisplay} onHide={hideModal} centered size="lg">
        <Modal.Body>
          <h3 className="mb-3 text-center">Overlay Data</h3>
          <p className="text-center mb-4">
            <Link to="https://pubmed.ncbi.nlm.nih.gov/27237738/" aria-label="SAFE analysis Publication URL"
                  title="SAFE analysis Publication URL" target="_blank">SAFE</Link> or <Link
            to="https://pubmed.ncbi.nlm.nih.gov/41423796/" aria-label="Risk Score Publication URL">RISK</Link> analysis
            identify regions of the
            global similarity
            network that are significantly enriched for a given set of genes. Upload a gene list and run analysis to
            identify network regions that are significantly enriched for your gene set. See <Link
            to="https://doi.org/10.1101/2025.06.30.662193"
            aria-label="Billman et al., 2026 Publication URL"
            title="Billman et al., 2026 Publication URL" target="_blank">Billman et al., 2026</Link> and <Link
            to="https://pubmed.ncbi.nlm.nih.gov/41423796/" aria-label="Risk Score Publication URL">Horecka & Röst,
            2026</Link> for
            examples.
          </p>

          <Form id="overlay-custom-form">
            <Row>
              {/* region Pills */}
              <Col sm={4}>
                <Nav variant="pills" activeKey={activeKey} className="flex-column w-100" role="tablist">
                  {customOverlayLists.map((list, i) => {
                    const warning = checkEntryForWarnings(i, list.parsedGenes);

                    return (
                      <Nav.Item className="d-flex align-items-center" key={list.id}>
                        <Nav.Link
                          eventKey={keyName(list.id)}
                          onClick={() => setActiveKey(keyName(list.id))}
                          role="button"
                          className="flex-grow-1 border border-dark-subtle mb-1"
                          style={{background: list.colour, color: getContrastColor(list.colour)}}>
                          {list.name}
                        </Nav.Link>
                        <CiWarning
                          aria-label={warning}
                          title={warning}
                          strokeWidth="1"
                          visibility={warning ? "visible" : "hidden"}
                          className="flex-shrink-0 text-danger"/>
                        <BsX
                          onClick={() => handleRemoveList(list.id)}
                          role="button"
                          aria-label="Remove this list"
                          title="Remove this list"
                          strokeWidth="1"
                          className="flex-shrink-0 text-danger"/>
                      </Nav.Item>
                    );
                  })}

                  <Nav.Item>
                    <Nav.Link
                      role="button"
                      aria-label="Add new gene list"
                      title="Add new gene list"
                      onClick={handleAddList}
                      className="px-1">
                      <BsPlus
                        role="button"
                        strokeWidth="1"/> Add gene list
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
              {/* endregion */}

              {/* region Tabs */}
              <Col sm={8}>
                <Tab.Container activeKey={activeKey}>
                  <Tab.Content>
                    {customOverlayLists.map((list, i) => {
                      const warning = checkEntryForWarnings(i, list.parsedGenes);

                      return (
                        <Tab.Pane key={`tab-${list.id}`} eventKey={keyName(list.id)}>
                          <div className="custom-entry">
                            <Form.Group className="mb-3" controlId={`name-${list.id}`}>
                              <Form.Label>Name</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder={customOverlayLabel(list.id)}
                                // If name is default, show empty placeholder instead
                                value={list.name === customOverlayLabel(list.id) ? "" : list.name}
                                onChange={handleNameChange(list.id)}
                              />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId={`genes-${list.id}`}>
                              <Form.Label>Gene List</Form.Label>
                              <Form.Control.Feedback
                                type="invalid"
                                style={{ // This prevents the modal to resize on error show/hide
                                  display: "block", // always occupy space
                                  visibility: warning ? "visible" : "hidden" // hide text when valid
                                }}
                              >
                                {warning}
                              </Form.Control.Feedback>
                              <Form.Control
                                as="textarea"
                                rows={5}
                                placeholder="Paste gene list, one per line or comma/space separated"
                                value={list.genes}
                                isInvalid={list.parsedGenes.length <= 2}
                                onChange={handleGeneListChange(list.id)}/>

                            </Form.Group>
                            <Form.Group className="mb-3" controlId={`examples-${list.id}`}>
                              <Form.Label>Example Datasets</Form.Label>
                              <div className="d-flex align-items-center gap-2 mt-2">
                                {/*<Button size="sm" variant="outline-secondary">Upload File</Button>*/}
                                {examples?.map((example) => (
                                  <Button
                                    size="sm"
                                    key={`example-${example.name}`}
                                    variant="outline-secondary"
                                    onClick={handleExample(list.id, example)}>
                                    {example.name}
                                  </Button>
                                ))}
                                <Form.Control type="file" accept=".txt" style={{display: "none"}}/>
                              </div>
                            </Form.Group>

                            <Form.Group className="mb-3" controlId={`color-${list.id}`}>
                              <Form.Label>Colour</Form.Label>
                              <Form.Control
                                type="color"
                                value={list.colour}
                                onChange={handleColourChange(list.id)}
                              />
                            </Form.Group>
                          </div>
                        </Tab.Pane>);
                    })}
                  </Tab.Content>
                </Tab.Container>
              </Col>
              {/* endregion */}
            </Row>
          </Form>

          {/* Footer buttons */}
          <div className="mt-2 d-flex justify-content-end gap-2">
            <Button variant="warning" onClick={handleReset}>Reset</Button>
            <Button variant="secondary" onClick={hideModal}>Close</Button>
            <Dropdown as={ButtonGroup}>
              <Button variant="primary" disabled={hasWarnings} onClick={handleSubmit(OverlayType.SAFE)}>Run
                SAFE</Button>
              <Dropdown.Toggle split variant="primary" disabled={hasWarnings}/>
              <Dropdown.Menu>
                <Dropdown.Item as={Button} onClick={handleSubmit(OverlayType.RISK)}>Run RISK</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default OverlayModal;
